const API_BASE = '/api';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function buildUrlWithParams(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  if (!params) return endpoint;
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `${endpoint}?${queryString}` : endpoint;
}

async function request<T>(
  pathWithOptionalQuery: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${pathWithOptionalQuery}`;
  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  };

  const response = await fetch(url, config);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errRecord = errorData as { message?: string; error?: string };
    const msg =
      (typeof errRecord.message === 'string' && errRecord.message) ||
      (typeof errRecord.error === 'string' && errRecord.error) ||
      `Request failed with status ${response.status}`;
    throw new ApiError(msg, response.status, errorData);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

async function requestBlob(pathWithOptionalQuery: string): Promise<Blob> {
  const url = `${API_BASE}${pathWithOptionalQuery}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errRecord = errorData as { message?: string; error?: string };
    const msg =
      (typeof errRecord.message === 'string' && errRecord.message) ||
      (typeof errRecord.error === 'string' && errRecord.error) ||
      `Request failed with status ${response.status}`;
    throw new ApiError(msg, response.status, errorData);
  }

  return response.blob();
}

export const api = {
  get: <T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>) => {
    const path = buildUrlWithParams(endpoint, params);
    return request<T>(path);
  },

  getBlob: (endpoint: string, params?: Record<string, string | number | boolean | undefined>) => {
    const path = buildUrlWithParams(endpoint, params);
    return requestBlob(path);
  },

  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string) =>
    request<T>(endpoint, {
      method: 'DELETE',
    }),
};

export { ApiError };
